/**
 * Resolve a taxonomy row's label for the active language.
 *
 * The cuisine/tag lookup rows store both an EN and RO label (never blank, the
 * schema requires both), so there's no fallback dance like the free-text
 * `pickLocalized` helper — just pick the column for the language.
 */

import type { Lang } from "@/i18n/translations";
import type { Labeled } from "@/types/taxonomy";

/** Pick the EN or RO label for a single cuisine/tag row. */
export function labelFor(row: Labeled, lang: Lang): string {
  return lang === "ro" ? row.label_ro : row.label_en;
}

/**
 * Resolve a list of slugs to localized labels, in the order the lookup defines
 * (not the order they're stored on the venue). Unknown slugs are dropped.
 */
export function labelsForSlugs<T extends Labeled & { slug: string }>(
  slugs: string[] | undefined,
  lookup: T[],
  lang: Lang,
): string[] {
  if (!slugs?.length) return [];
  const set = new Set(slugs);
  return lookup.filter((row) => set.has(row.slug)).map((row) => labelFor(row, lang));
}
